import React, { useEffect, useMemo, useState } from "react";
import PortfolioTrackerHeader from "../header";
import { MEMBER_KEY, MyTrackerTabs, USER_DETAIL } from "../../../utils/constants";
import { Filters } from "../Filters";
import { httpPost } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import EquityChart from "./EquityGraph";
import REIToggablePanel from "../../../components/ui/toggablePanel";
import { REIProgressSpinner } from "../../../components/ui";
import ErrorMessage from "../../../components/ui/errorMessage";
import InputSlider from "../../../components/ui/slider";
import REIToggleButton from "../../../components/ui/toggleButton";
import EquityDetail from "./EquityDetails";
import { ITrackerEquity } from "../../../interface/equity";
import { IActual4 } from "../../../interface/myPortfolio";
import { useFilterSelection } from "../FilterSelectionContext";
import { getCurrentFinancialYear } from "../../../utils/commonUtil";
import { RadioButton } from "primereact/radiobutton";

const MyEquity: React.FC = () => {
  const [currentYear, setCurrentYear] = useState<number>(Number(getCurrentFinancialYear()));
  const [isForecastOnly, setIsForecastOnly] = useState<boolean>(false);
  const { selectedProfiles, selectedEntityId, isShowingTotals, selectedYear, setSelectedYear } = useFilterSelection();
  const memberId = localStorage.getItem(MEMBER_KEY);
  const memberDetail = JSON.parse(localStorage.getItem(USER_DETAIL) || "{}");
  const [equityData, setEquityData] = useState<ITrackerEquity>();
  const [selectedProperty, setSelectedProperty] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [dataExist, setDataExist] = useState<boolean>(false);

  useEffect(() => {
    setLoading(true);
    const ProfileData =
      selectedProfiles.length > 0
        ? selectedProfiles.map((profile) => ({
            ProfileId: profile.MemberProfileId,
          }))
        : [];
    const Entity = selectedEntityId != 0 ? [{ EntityId: selectedEntityId }] : [];
    const payload = {
      MemberId: memberId,
      UserCountryCode: memberDetail.Country,
      Entity: Entity,
      Profiles: ProfileData,
      MemberName: memberDetail.FirstName + " " + memberDetail.LastName,
    };
    async function getEquityData() {
      httpPost(`${ApiEndPoints.tracker}PortfolioTracker/Equity`, payload)
        .then((response: any) => {
          const equityResponseData = response?.data ?? {};
          const propertyDetails = Array.isArray(equityResponseData.PropertyDetails)
            ? equityResponseData.PropertyDetails
            : [];

          setEquityData({ ...equityResponseData, isPropertyClickable: true });
          setDataExist(propertyDetails.length > 0);

          if (propertyDetails.length > 0) {
            const lowestYearRow = findLowestYearRow(propertyDetails);
            setCurrentYear(lowestYearRow ? Number((lowestYearRow as any).Year.slice(2, 4)) : currentYear);
          }

          setSelectedProperty(1);
        })
        .catch((error) => {
          return <ErrorMessage message="error" />;
        })
        .finally(() => {
          setLoading(false);
        });
    }
    getEquityData();
  }, [selectedProfiles, selectedEntityId]);

  const findLowestYearRow = (data: any[]) => {
    let lowestYearRow: { Year: any } | null = null;

    data.forEach((item) => {
      const actuals = item.EquityGraphPortions?.Actuals || [];
      actuals.forEach((actual: any) => {
        if (!lowestYearRow || Number(actual.Year) < Number(lowestYearRow.Year)) {
          lowestYearRow = actual;
        }
      });
    });

    return lowestYearRow;
  };

  const [filteredData, setFilteredData] = useState<{ Actuals: IActual4[]; Forecast: IActual4[] }>({
    Actuals: [],
    Forecast: [],
  });

  useEffect(() => {
    if (!equityData) {
      setFilteredData({ Actuals: [], Forecast: [] });
      return;
    }

    const propertyDetails = Array.isArray(equityData.PropertyDetails) ? equityData.PropertyDetails : [];
    const entityIdToCompare = selectedEntityId === 0 ? "" : selectedEntityId.toString();
    let newFilteredData: { Actuals: IActual4[]; Forecast: IActual4[] } = { Actuals: [], Forecast: [] };

    if (selectedProperty === 1) {
      const totalData = isShowingTotals
        ? equityData.TotalForGraph?.find((x) => x.EntityId == entityIdToCompare)?.Totals
        : equityData.TotalForGraph?.find((x) => x.EntityId == entityIdToCompare)?.Portions;
      if (totalData) {
        newFilteredData = {
          Actuals: Array.isArray(totalData.Actuals) ? totalData.Actuals : [],
          Forecast: Array.isArray(totalData.Forecast) ? totalData.Forecast : [],
        };
      }
    } else {
      const propertyData = propertyDetails.find(
        (property) => property.EquityPropertyDetail.PortfolioTrackerId === selectedProperty
      );

      const totalActuals = Array.isArray(propertyData?.EquityGraphTotals?.Actuals)
        ? propertyData.EquityGraphTotals.Actuals
        : [];
      const totalForecast = Array.isArray(propertyData?.EquityGraphTotals?.Forecast)
        ? propertyData.EquityGraphTotals.Forecast
        : [];
      const portionActuals = Array.isArray(propertyData?.EquityGraphPortions?.Actuals)
        ? propertyData.EquityGraphPortions.Actuals
        : [];
      const portionForecast = Array.isArray(propertyData?.EquityGraphPortions?.Forecast)
        ? propertyData.EquityGraphPortions.Forecast
        : [];

      if (propertyData) {
        if (isShowingTotals) {
          newFilteredData = {
            Actuals: totalActuals.map((data) => {
              return {
                MarketValue: data.MarketValue,
                LoanValue: data.LoanValue,
                Equity: data.Equity,
                Year: data.Year,
              };
            }),
            Forecast: totalForecast.map((data) => {
              return {
                MarketValue: data.MarketValue,
                LoanValue: data.LoanValue,
                Equity: data.Equity,
                Year: data.Year,
              };
            }),
          };
        } else {
          newFilteredData = {
            Actuals: portionActuals.map((data) => {
              return {
                MarketValue: data.MarketValue,
                LoanValue: data.LoanValue,
                Equity: data.Equity,
                Year: data.Year,
              };
            }),
            Forecast: portionForecast.map((data) => {
              return {
                MarketValue: data.MarketValue,
                LoanValue: data.LoanValue,
                Equity: data.Equity,
                Year: data.Year,
              };
            }),
          };
        }
      }
    }
    setFilteredData(newFilteredData);
  }, [selectedProperty, equityData, selectedEntityId, selectedProfiles, isShowingTotals]);

  const handleSelectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedProperty(Number(event.target.value));
  };

  const HeaderTemplate = (
    <div className="rounded-t-lg p-2 px-4 bg-slate-600 text-white border-solid border-gray-100 border-x border-y flex gap-3 justify-around">
      <div className="flex w-1/3 content-start">
        <h3>Equity in detail</h3>
      </div>
      <div className="flex flex-row gap-3 align-middle w-1/3 content-center">
        <h3>Showing:</h3>
        <InputSlider
          id="meq-sldr-year"
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.value)}
          min={currentYear}
          max={currentYear + (currentYear < Number(getCurrentFinancialYear()) ? 10 : 9)}
          step={1}
          classNames="self-center w-1/2"
        />
        <h3>
          FY-{selectedYear}
          <span className="text-sm">
            {selectedYear.toString() === getCurrentFinancialYear() ? " (Actuals YTD)" : ""}
          </span>
        </h3>
      </div>
      <div className="w-1/3 content-center text-end ">
        <REIToggleButton
          id="meq-tgl-forecast"
          customclass="flex-end mr-2"
          index=""
          isSelected={isForecastOnly}
          offlabel="Show only forecasts"
          onToggleChange={() => setIsForecastOnly(!isForecastOnly)}
          onlabel="Show actuals & forecasts"
        />
      </div>
    </div>
  );

  const renderContent = () => {
    return (
      <>
        <div className="flex flex-col flex-wrap gap-4 justify-around h-[500px]">
          <div className="flex-1 w-1/2 h-full">
            <EquityChart
              trackerData={filteredData}
              year={Number("20" + selectedYear.toString())}
              onYearChangeCallback={(year) => setSelectedYear(year)}
            ></EquityChart>
          </div>
          <div className="flex-1 w-1/2 flex-col gap-2 h-full justify-center">
            <div className="font-semibold text-xl pb-2">Showing equity summary for:</div>
            <div className="max-h-[450px] overflow-y-auto">
              <div className=" text-left text-base text-slate-500">
                <div className="flex gap-3 mb-2">
                  <RadioButton
                    inputId="property-total"
                    data-testid="meq-rdo-allproperties"
                    type="radio"
                    id="property-total"
                    name="property"
                    value="1"
                    checked={selectedProperty.toString() === "1"}
                    onChange={(e) => handleSelectionChange(e)}
                  />
                  <label htmlFor="property-total" className="ml-2">
                    All properties
                  </label>
                </div>

                {equityData &&
                  equityData?.PropertyDetails?.map((property, index) => {
                    const address =
                      [
                        property.EquityPropertyDetail.StreetAddress,
                        property.EquityPropertyDetail.Suburb,
                        property.EquityPropertyDetail.District,
                        property.EquityPropertyDetail.State,
                        property.EquityPropertyDetail.Postcode,
                      ]
                        .filter((part) => part)
                        .join(", ") + ` (${property.EquityPropertyDetail.Country})`;

                    const uniqueId = `${property.EquityPropertyDetail.PortfolioTrackerId}-${index}`;

                    return (
                      <div key={uniqueId} className="flex items-center gap-3 mb-2">
                        <RadioButton
                          inputId={uniqueId}
                          data-testid={`meq-rdo-property-${uniqueId}`}
                          name="property"
                          value={property.EquityPropertyDetail.PortfolioTrackerId}
                          onChange={(e) => setSelectedProperty(e.value)}
                          checked={selectedProperty.toString() === uniqueId.split("-")[0]}
                        />
                        <label htmlFor={uniqueId} className="ml-2 break-words truncate">
                          {address}
                        </label>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      {loading && (
        <div className="content-center flex justify-center items-center min-h-[100px]">
          <REIProgressSpinner></REIProgressSpinner>
        </div>
      )}
      {equityData && !loading && (
        <div className="align-middle hide-header">
          {!loading && (
            <>
              <PortfolioTrackerHeader
                tabIndex={MyTrackerTabs.MyEquity}
                tabName="My Equity"
                hasProperties={dataExist}
                children={<Filters tab="MyEquity"></Filters>}
              ></PortfolioTrackerHeader>
              {!dataExist && (
                <REIToggablePanel
                  contentComponent={
                    <div className="flex flex-col">
                      <div className="mb-4 text-left">
                        Start adding your investment properties to Portfolio Tracker and start measuring your cash flow
                        forecasts against its actuals.
                      </div>
                    </div>
                  }
                  header="Equity"
                  istoggleable={false}
                  defaultCollapse={false}
                ></REIToggablePanel>
              )}
              {dataExist && (
                <REIToggablePanel
                  contentComponent={renderContent()}
                  defaultCollapse={false}
                  header="Equity"
                  istoggleable={false}
                  customClassName="align-middle bg-gray-100"
                ></REIToggablePanel>
              )}
              <REIToggablePanel
                contentComponent={
                  dataExist ? (
                    <EquityDetail
                      isFooterVisible={true}
                      equityData={equityData}
                      isForecastOnly={isForecastOnly}
                      year={"20" + selectedYear.toString()}
                    />
                  ) : (
                    <div className="flex flex-col">
                      <div className="mb-4 text-left">
                        Start adding your investment properties to Portfolio Tracker and start measuring your cash flow
                        forecasts against its actuals.
                      </div>
                    </div>
                  )
                }
                defaultCollapse={false}
                header=""
                customClassName="mt-2"
                istoggleable={false}
                customHeader={HeaderTemplate}
              ></REIToggablePanel>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default MyEquity;
